import {
  useTheme,
  Dialog,
  DialogTitle,
  FormHelperText,
  Typography,
  Button,
  Box,
  InputLabel,
  IconButton,
} from "@mui/material";
import * as yup from "yup";
import { Formik, Form } from "formik";
import OtpInput from "react-otp-input";
import { useEffect, useState } from "react";
import { ReactComponent as CloseOutlined } from "../../assets/icons/close_icon.svg";
import { ReactComponent as BackOutlined } from "../../assets/icons/back_icon.svg";
import { useDispatch } from "react-redux";
import { doSendOtp, doVerifyOtp } from "../../reducers/auth/auth.service";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { enqueueSnackbar } from "notistack";

const VerificationDialog = ({
  open,
  handleDialogClose,
  handleOpenLogin,
  originalEmail,
  maskedEmail,
}) => {
  const theme = useTheme();
  const [timer, setTimer] = useState(15);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const changeLayout = useMediaQuery("(max-width: 500px)");

  const validationSchema = yup.object({
    otp: yup
      .string()
      .min(6, "Please enter valid OTP")
      .required("Please enter valid OTP"),
  });

  const initialValues = {
    otp: "",
  };

  // Handle timer
  useEffect(() => {
    if (open && timer > 0) setTimeout(() => setTimer(timer - 1), 1000);
    else if (!open) setTimer(15);
  }, [timer, open]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
        onSubmit={async (values, { setFieldError, resetForm }) => {
          const responseValue = await dispatch(
            doVerifyOtp({ email: originalEmail, otp: values.otp })
          );
          if (doVerifyOtp.fulfilled.match(responseValue)) {
            if (!responseValue.payload.firstTimeUser) {
              enqueueSnackbar("Login Successful", { variant: "success" });
            }
            resetForm();
            handleDialogClose();
            navigate("/");
          } else {
            setFieldError("otp", "Please enter valid OTP");
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
          resetForm,
        }) => (
          <Form>
            <Dialog
              scroll="body"
              open={open}
              onClose={(e, reason) => {
                if (reason && reason === "backdropClick") return;
                resetForm();
                handleDialogClose();
              }}
              fullWidth
              disableScrollLock={false}
              slotProps={{
                paper: {
                  component: "form",
                  sx: {
                    px: { xs: "20px !important", sm: "56px !important" },
                    borderRadius: {
                      xs: "20px !important",
                      sm: "50px !important",
                    },
                  },
                },
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <DialogTitle
                  variant="h3"
                  sx={{
                    p: 0,
                    mt: "15px",
                    fontWeight: 700,
                  }}
                >
                  VERIFY OTP
                </DialogTitle>

                <Typography
                  sx={{
                    color: "white",
                    // textAlign: "center",
                    fontFamily: "secondaryFontFamily",
                    fontSize: "14px",
                    fontWeight: 300,
                    mb: "50px",
                  }}
                >
                  {`Verification code sent to ${maskedEmail}`}
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    resetForm();
                    handleDialogClose();
                  }}
                  sx={{
                    position: "absolute",
                    right: 20,
                    top: 20,
                    fontSize: "25px",
                    color: "#999999",
                  }}
                >
                  <CloseOutlined />
                </IconButton>

                <IconButton
                  aria-label="back"
                  onClick={() => {
                    resetForm();
                    handleDialogClose();
                    handleOpenLogin();
                  }}
                  sx={{
                    position: "absolute",
                    left: 20,
                    top: 20,
                    fontSize: "25px",
                    color: "#999999",
                  }}
                >
                  <BackOutlined />
                </IconButton>
                <Box sx={{ maxWidth: "384px", mx: "auto", textAlign: "left" }}>
                  <InputLabel sx={{ mb: "5px" }}>Enter OTP</InputLabel>
                  <OtpInput
                    value={values.otp}
                    className="otp-input"
                    onChange={(otp) => {
                      setFieldValue("otp", otp);
                      setFieldTouched("otp", true);
                    }}
                    numInputs={6}
                    shouldAutoFocus
                    placeholder="000000"
                    inputType="number"
                    containerStyle={{
                      gap: "12px",
                      width: "100%",
                    }}
                    inputStyle={{
                      color: errors.otp ? "#DB0000" : "white",
                      width: "20%",
                      height: "55px",
                      borderRadius: changeLayout ? "10px" : "100%",
                      backgroundColor: "transparent",
                      border: errors.otp
                        ? "1px solid #DB0000"
                        : "1px solid #FFFFFF33",
                    }}
                    renderInput={(props) => (
                      <input
                        className="custom-otp-input"
                        {...props}
                        autoFocus
                      />
                    )}
                  />

                  {touched.otp && Boolean(errors.otp) && (
                    <FormHelperText
                      sx={{
                        color: "error.main",
                      }}
                    >
                      {touched.otp && errors.otp}
                    </FormHelperText>
                  )}
                </Box>

                <Typography
                  sx={{
                    fontFamily: "secondaryFontFamily",
                    color: "white",
                    fontSize: "14px",
                    mt: "20px",
                  }}
                >
                  Haven't received the OTP?{" "}
                  <Button
                    disabled={timer !== 0}
                    onClick={async () => {
                      setTimer(15);
                      dispatch(doSendOtp({ email: originalEmail }));
                    }}
                    variant="text"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      fontFamily: "secondaryFontFamily",
                      textTransform: "none !important",
                      minWidth: "0px !important",
                      verticalAlign: "unset !important",
                      p: 0,
                      fontSize: "14px !important",
                      ":disabled": {
                        color: "#0CAF6066 !important",
                      },
                    }}
                  >
                    Resend
                  </Button>
                </Typography>

                <Typography
                  sx={{
                    color: "white",
                    fontFamily: "secondaryFontFamily",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  00:{timer > 9 ? `${timer}` : `0${timer}`}
                </Typography>

                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    mt: "50px",
                    mb: "30px",
                    width: "105px",
                    ml: "auto",
                    p: "10px 10px 6px 10px !important",
                  }}
                >
                  Verify
                </Button>
              </Box>
            </Dialog>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default VerificationDialog;
